/**
 * Date Input Control
 * Date picker field
 * Performance: Memoized, useCallback for event handlers
 */

import React, { useCallback, memo } from 'react';
import { WarningIcon } from '@atlas-forms/ui-components-react';
import type { ControlRendererProps } from '../ControlRenderer';
import { getBaseControlClassName } from '../utils/classNameUtils';
import { hasOversizedYear } from '../../utils/dateYearLimit';

const DateInput = memo(React.forwardRef<HTMLInputElement, ControlRendererProps>(
  (
    {
      control,
      value,
      onChange,
      onBlur,
      onFocus,
      error,
      touched,
      disabled,
      mode,
    },
    ref,
  ) => {
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        // A year of more than four digits is not a valid date. Keeping the previous value lets React
        // restore the input, so the bad year is never stored.
        if (hasOversizedYear(e.target.value)) return;
        onChange(e.target.value || undefined);
      },
      [onChange],
    );

    const showError = touched && error;
    const containerClassName = getBaseControlClassName(control, {
      error: showError,
      disabled: disabled || control.disabled,
    });

    // Get min/max dates from control config if specified
    const min = (control.config?.min as string) || undefined;
    const max = (control.config?.max as string) || undefined;

    return (
      <div className={containerClassName}>
        {control.label && (
          <label htmlFor={control.id} className="control__label">
            <span className="control__label-text">{control.label}</span>
            {control.required && (
              <span className="control__required" aria-label="required">
                *
              </span>
            )}
          </label>
        )}

        <div className="control__input-wrapper">
          <input
            ref={ref}
            id={control.id}
            type="date"
            name={control.id}
            value={value || ''}
            onChange={handleChange}
            onBlur={onBlur}
            onFocus={onFocus}
            placeholder={control.placeholder || 'YYYY-MM-DD'}
            disabled={disabled || control.disabled}
            readOnly={mode === 'view' || control.readonly}
            min={min}
            max={max}
            className="control__input control__input--date"
            aria-invalid={showError ? 'true' : 'false'}
            aria-required={control.required ? 'true' : 'false'}
            aria-describedby={`${control.id}-error ${control.id}-help`}
          />
          {showError && (
            <span className="control__error-icon" aria-hidden="true">
              <WarningIcon width={14} height={14} />
            </span>
          )}
        </div>

        {control.helpText && (
          <div id={`${control.id}-help`} className="control__help">
            {control.helpText}
          </div>
        )}

        {showError && (
          <div
            id={`${control.id}-error`}
            className="control__error-message"
            role="alert"
          >
            {error}
          </div>
        )}
      </div>
    );
  },
));

DateInput.displayName = 'DateInput';

export default DateInput;
